import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { situations } from '../../data/guide/statGuide';
import { classItems, itemRequirementText } from '../../utils/classBuilds';
import { itemPath } from '../../utils/worldFormat';
import { normalize } from '../../utils/text';
import { classBuildPath } from '../../data/guide/classBuilds';
import { ItemThumb } from '../world/ItemSprite';
import type { ClassBuildGuide } from '../../types/classBuild';
import type { CharacterClass } from '../../types/progression';
import type { WorldData } from '../../types/world';
const SUPPORT = /apoyo|bufeadora|buffs|debilitamientos/i;

/** "¿Qué debería mejorar ahora?": consejos por situación, sin cálculos simulados. */
export function WhatToImprove({
  cls,
  guide,
  w
}: {
  cls?: CharacterClass;
  guide?: ClassBuildGuide;
  w?: WorldData;
}) {
  const [id, setId] = useState(situations[0].id);
  const [q, setQ] = useState('');
  const sit = situations.find(s => s.id === id) ?? situations[0];
  const matches = useMemo(() => {
    const nq = normalize(q);
    if (!w || !cls || nq.length < 3) return [];
    return classItems(w, cls).items.filter(ci => ci.section !== 15 && normalize(ci.name).includes(nq)).slice(0, 6);
  }, [w, cls, q]);
  const supportStyles = guide?.styles.filter(s => SUPPORT.test(`${s.name} ${s.summary}`)) ?? [];
  return <section id="que-mejorar" aria-labelledby="que-mejorar-t" className="scroll-mt-24" data-id="element-446" data-mp="1vvvvtnkbf0s5">
      <h2 id="que-mejorar-t" className="font-cinzel text-lg font-bold text-white md:text-2xl" data-id="element-447" data-mp="yagcaujzfnrt">
        ¿Qué debería mejorar ahora?
      </h2>
      <p className="mt-1 text-sm text-gray-400" data-id="element-448" data-mp="1872x5jcb6etu">Elige lo que te pasa. Son consejos, no cálculos de daño.</p>

      <div className="mt-4 grid gap-6 md:grid-cols-[16rem_1fr]" data-id="element-449" data-mp="1d5wc7g78udw4">
        <div role="radiogroup" aria-label="Situación" className="flex flex-wrap gap-2 md:flex-col md:flex-nowrap" data-id="element-450" data-mp="1d431nptif9iu">
          {situations.map(s => {
          const active = s.id === sit.id;
          return <button key={s.id} type="button" role="radio" aria-checked={active} onClick={() => setId(s.id)} className={`rounded-xl border px-3 py-2 text-left text-sm transition-colors duration-150 ${active ? 'border-[#D4AF37] bg-[#D4AF37]/10 font-semibold text-[#D4AF37]' : 'border-white/10 text-gray-200 hover:border-[#D4AF37]/40'}`} data-id="element-451" data-mp="oj396j657oyk">
                {s.label}
              </button>;
        })}
        </div>

        <div aria-live="polite" className="min-w-0" data-id="element-452" data-mp="uaculllzf3f">
          <h3 className="font-semibold text-white" data-id="element-453" data-mp="ro1sbziehgg">{sit.label}</h3>
          <ul className="mt-2 space-y-2" data-id="element-454" data-mp="emgphxpjz3y4">
            {sit.advice.map(a => <li key={a} className="flex gap-2 text-sm text-gray-300" data-id="element-455" data-mp="9t4cyly5enlq">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D4AF37]" aria-hidden="true" data-id="element-456" data-mp="3i9qqha1mza7q" />
                {a}
              </li>)}
          </ul>

          {sit.itemCheck && <div className="mt-4" data-id="element-457" data-mp="343x6qc4mjzzn">
              {cls && w ? <>
                  <label htmlFor="item-check" className="text-sm font-semibold text-white" data-id="element-458" data-mp="38ux57lte6q49">
                    Busca el ítem que quieres equipar ({cls.base})
                  </label>
                  <input id="item-check" value={q} onChange={e => setQ(e.target.value)} placeholder="Ej.: Kris, Leather Armor…" className="mt-2 w-full rounded-xl border border-white/15 bg-[#050b14] px-3 py-2.5 text-sm text-white placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none" data-id="element-459" data-mp="2u4g6ia856eb2" />
                  {q.trim().length >= 3 && <ul className="mt-3 space-y-2" data-id="element-460" data-mp="3huafuht95kqk">
                      {matches.length ? matches.map(ci => <li key={ci.id} className="flex items-center gap-3" data-id="element-461" data-mp="26ggfraatynwh">
                            <ItemThumb id={ci.id} name={ci.name} size="thumb" data-id="element-462" data-mp="26ec240n4qaj0" />
                            <span className="min-w-0" data-id="element-463" data-mp="2mp5to7brhr79">
                              <Link to={itemPath(ci.id)} className="block truncate text-sm font-semibold text-white hover:text-[#D4AF37]" data-id="element-464" data-mp="2hwurextjttbj">
                                {ci.name}
                              </Link>
                              <span className="block text-xs text-gray-400" data-id="element-465" data-mp="2809m0bdmag76">Requisito documentado: {itemRequirementText(ci.item)}</span>
                            </span>
                          </li>) : <li className="text-sm text-gray-500" data-id="element-466" data-mp="2voj16ugbs5pc">Ningún ítem compatible con {cls.base} coincide con esa búsqueda.</li>}
                    </ul>}
                </> : <p className="text-sm text-gray-500" data-id="element-467" data-mp="1kafabx51axoi">Elige tu clase arriba para buscar el ítem y ver sus requisitos documentados.</p>}
            </div>}

          {sit.supportStyles && cls && supportStyles.length > 0 && <div className="mt-4" data-id="element-468" data-mp="3qvs23ptyvyu1">
              <p className="text-sm font-semibold text-white" data-id="element-469" data-mp="3vw6chej1jkv8">Estilos de apoyo de {cls.base}</p>
              <ul className="mt-2 space-y-1" data-id="element-470" data-mp="4pwhiu4jclow">
                {supportStyles.map(s => <li key={s.id} data-id="element-471" data-mp="9gv4gck14i14">
                    <Link to={classBuildPath(cls.id, s.id, 'estilos')} className="text-sm text-[#D4AF37] hover:underline" data-id="element-472" data-mp="9hwo35f42cug">
                      {s.name}
                    </Link>
                    <span className="text-sm text-gray-400" data-id="element-473" data-mp="ih1mmbqoa66m"> · {s.summary}</span>
                  </li>)}
              </ul>
            </div>}
        </div>
      </div>
    </section>;
}